import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { ProfileCardSkeleton, StatCardSkeleton, ListItemSkeleton } from "../../components/ui/skeleton-loaders";
import { toast } from "sonner";
import { 
  User, 
  Crown, 
  Flame, 
  Target,
  TrendingUp,
  Calendar,
  Award,
  ChevronRight,
  Dumbbell,
  Utensils,
  LogOut,
  Settings,
  Zap,
  Trophy,
  Scale,
  ChefHat,
  Camera,
  Loader2
} from "lucide-react";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Streak tiers with messages
const STREAK_TIERS = [
  { min: 0, name: "Empezando", color: "text-gray-400", bg: "bg-gray-500/10" },
  { min: 3, name: "Construyendo", color: "text-blue-400", bg: "bg-blue-500/10" },
  { min: 7, name: "En racha", color: "text-green-400", bg: "bg-green-500/10" },
  { min: 14, name: "Imparable", color: "text-purple-400", bg: "bg-purple-500/10" },
  { min: 30, name: "Leyenda", color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/10" },
];

const getStreakTier = (days) => {
  for (let i = STREAK_TIERS.length - 1; i >= 0; i--) {
    if (days >= STREAK_TIERS[i].min) return STREAK_TIERS[i];
  }
  return STREAK_TIERS[0];
};

const StreakCard = ({ currentStreak, bestStreak }) => {
  const tier = getStreakTier(currentStreak);
  
  return (
    <div className={cn(
      "rounded-2xl p-4 border border-[#2A2A2A]",
      currentStreak > 0 ? "bg-gradient-to-br from-[#D4AF37]/10 to-[#0B0B0B]" : "bg-[#1A1A1A]"
    )}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", tier.bg)}>
            <Flame className={cn("w-5 h-5", tier.color)} />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">Racha actual</h3>
            <p className={cn("text-xs", tier.color)}>{tier.name}</p>
          </div>
        </div>
        <div className="text-right">
          <div className={cn("text-3xl font-black", currentStreak > 0 ? "text-[#D4AF37]" : "text-[#666]")}>
            {currentStreak}
          </div>
          <div className="text-[#666] text-xs">días</div>
        </div>
      </div>
      
      {/* Best streak */}
      <div className="flex items-center justify-between pt-3 border-t border-[#2A2A2A]">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#666]" />
          <span className="text-[#888] text-xs">Mejor racha</span>
        </div>
        <span className="text-white font-bold text-sm">{bestStreak} días</span>
      </div>
    </div>
  );
};

// Badge achievements
const BADGES = [
  { id: 'first', days: 1, icon: '🏃', name: 'Primer Entreno', desc: 'Completaste tu primer entrenamiento' },
  { id: 'week', days: 7, icon: '🔥', name: 'Semana Fuerte', desc: '7 días consecutivos' },
  { id: 'twoweeks', days: 14, icon: '💪', name: 'Imparable', desc: '2 semanas seguidas' },
  { id: 'month', days: 30, icon: '🏆', name: 'Leyenda', desc: '30 días consecutivos' },
  { id: 'quarter', days: 90, icon: '👑', name: 'GOAT', desc: '3 meses sin parar' },
];

const BadgesSection = ({ bestStreak, totalWorkouts }) => {
  const earnedBadges = BADGES.filter(b => bestStreak >= b.days || (b.days === 1 && totalWorkouts >= 1));
  const nextBadge = BADGES.find(b => bestStreak < b.days && (b.days > 1 || totalWorkouts < 1));
  
  if (earnedBadges.length === 0 && !nextBadge) return null;
  
  return (
    <div className="bg-[#1A1A1A] rounded-2xl p-4 border border-[#2A2A2A]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <Award className="w-4 h-4 text-[#D4AF37]" />
          Logros
        </h3>
        <span className="text-[#666] text-xs">{earnedBadges.length}/{BADGES.length}</span>
      </div>
      
      {/* Earned badges */}
      {earnedBadges.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {earnedBadges.map(badge => (
            <div 
              key={badge.id}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/30"
              title={badge.desc}
            >
              <span className="text-base">{badge.icon}</span>
              <span className="text-[#D4AF37] text-xs font-medium">{badge.name}</span>
            </div>
          ))}
        </div>
      )}
      
      {/* Next badge to unlock */}
      {nextBadge && (
        <div className="flex items-center justify-between pt-3 border-t border-[#2A2A2A]">
          <div className="flex items-center gap-2">
            <span className="text-lg opacity-40">{nextBadge.icon}</span>
            <div>
              <p className="text-[#666] text-xs">Próximo logro</p>
              <p className="text-white text-xs font-medium">{nextBadge.name}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[#D4AF37] text-xs font-bold">{nextBadge.days - bestStreak} días</p>
            <p className="text-[#666] text-[10px]">para desbloquear</p>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, unit, color = "text-[#D4AF37]" }) => (
  <div className="bg-[#1A1A1A] rounded-xl p-4 border border-[#2A2A2A]">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={cn("w-4 h-4", color)} />
      <span className="text-[#888] text-xs">{label}</span>
    </div>
    <div className="flex items-baseline gap-1">
      <span className="text-white font-bold text-xl">{value}</span>
      {unit && <span className="text-[#666] text-sm">{unit}</span>}
    </div>
  </div>
);

const QuickActionCard = ({ icon: Icon, label, sublabel, onClick, highlight = false }) => (
  <button
    onClick={onClick}
    className={cn(
      "w-full p-4 rounded-xl border flex items-center gap-3 transition-all active:scale-[0.98]",
      highlight 
        ? "bg-gradient-to-r from-[#D4AF37]/10 to-transparent border-[#D4AF37]/30" 
        : "bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#3A3A3A]"
    )}
  >
    <div className={cn(
      "w-10 h-10 rounded-xl flex items-center justify-center",
      highlight ? "bg-[#D4AF37]/20" : "bg-[#2A2A2A]"
    )}>
      <Icon className={cn("w-5 h-5", highlight ? "text-[#D4AF37]" : "text-white")} />
    </div>
    <div className="flex-1 text-left">
      <div className="text-white font-medium text-sm">{label}</div>
      {sublabel && <div className="text-[#666] text-xs">{sublabel}</div>}
    </div>
    <ChevronRight className="w-4 h-4 text-[#666]" />
  </button>
);

const PerfilPage = () => {
  const navigate = useNavigate();
  const { user, isPremium, logout, getAuthHeaders } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const fetchProfileData = useCallback(async () => {
    try {
      // Fetch user profile and stats
      const [meResponse, historyResponse] = await Promise.all([
        axios.get(`${API_URL}/api/auth/me`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/workout/history`, { headers: getAuthHeaders() })
      ]);
      
      setProfileData(meResponse.data);
      setStatsData({
        current_streak: historyResponse.data.streak || 0,
        best_streak: historyResponse.data.best_streak || 0,
        total_workouts: historyResponse.data.total || 0,
        history: historyResponse.data.history || []
      });
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  const profile = profileData?.profile || {};
  const currentStreak = statsData?.current_streak || 0;
  const bestStreak = statsData?.best_streak || 0;
  const totalWorkouts = statsData?.total_workouts || 0;

  // Handle photo upload - optimized for mobile/Safari
  const handlePhotoSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input for Safari
    event.target.value = '';

    // Validate file type (JPG, PNG, WEBP)
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      toast.error('Formato no válido. Usa JPG, PNG o WEBP');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen es demasiado grande (máx 2MB)');
      return;
    }

    setUploadingPhoto(true);

    try {
      // Compress and convert to base64 using canvas
      const compressImage = (file, maxWidth = 400, quality = 0.8) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;
            
            // Resize if too large
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
            
            canvas.width = width;
            canvas.height = height;
            
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to base64 JPEG for smaller size
            const base64 = canvas.toDataURL('image/jpeg', quality);
            resolve(base64);
          };
          img.onerror = () => reject(new Error('Error al cargar imagen'));
          
          // Read file as data URL
          const reader = new FileReader();
          reader.onload = (e) => {
            img.src = e.target.result;
          };
          reader.onerror = () => reject(new Error('Error al leer archivo'));
          reader.readAsDataURL(file);
        });
      };

      const base64Data = await compressImage(file);
      
      // Upload to server
      await axios.put(
        `${API_URL}/api/member/profile/avatar`,
        { avatar_url: base64Data },
        { headers: getAuthHeaders() }
      );
      
      // Update local state
      setProfileData(prev => ({
        ...prev,
        profile: { ...prev?.profile, avatar_url: base64Data }
      }));
      
      toast.success('Foto actualizada');
    } catch (error) {
      console.error('Error uploading photo:', error);
      const errorMsg = error?.response?.data?.detail || 'Error al subir la foto. Intenta de nuevo.';
      toast.error(errorMsg);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Get user level display
  const getLevelDisplay = () => {
    const level = profile?.level;
    if (!level) return "Sin nivel";
    const levels = {
      principiante: "Principiante",
      intermedio: "Intermedio",
      avanzado: "Avanzado",
      goat: "GOAT"
    };
    return levels[level.toLowerCase()] || level;
  };

  // Get gender display
  const getGenderDisplay = () => {
    const gender = profile?.gender || profile?.track;
    if (!gender) return "";
    if (gender === "men" || gender === "hombre") return "Hombre";
    if (gender === "women" || gender === "mujer") return "Mujer";
    return gender;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] p-4 pb-24">
        <ProfileCardSkeleton />
        <div className="mt-6 grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
        <div className="mt-6 space-y-3">
          {[1, 2, 3].map(i => (
            <ListItemSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] pb-24" data-testid="perfil-page">
      {/* Hidden file input for photo upload - optimized for mobile */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoSelect}
        accept="image/jpeg,image/png,image/webp,image/jpg"
        capture="user"
        className="hidden"
      />
      
      {/* Header */}
      <div className="p-4">
        {/* Profile Card */}
        <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0B0B0B] rounded-2xl p-5 border border-[#2A2A2A] mb-6">
          <div className="flex items-start gap-4">
            {/* Avatar with photo support */}
            <div className="relative">
              {/* Avatar circle */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="w-18 h-18 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center overflow-hidden active:scale-95 transition-transform"
                style={{ width: '72px', height: '72px' }}
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-6 h-6 text-black animate-spin" />
                ) : profile?.avatar_url ? (
                  <img 
                    src={profile.avatar_url} 
                    alt="Avatar" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  // Show initials or default icon
                  <span className="text-black font-black text-2xl">
                    {user?.email?.charAt(0)?.toUpperCase() || <User className="w-8 h-8 text-black" />}
                  </span>
                )}
              </button>
              
              {/* Camera icon for photo upload */}
              <button 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute -bottom-1 -left-1 w-7 h-7 rounded-full bg-[#2A2A2A] flex items-center justify-center border-2 border-[#0B0B0B] hover:bg-[#3A3A3A] transition-colors"
              >
                <Camera className="w-3.5 h-3.5 text-white" />
              </button>
              
              {/* Premium badge */}
              {isPremium && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center border-2 border-[#0B0B0B]">
                  <Crown className="w-3 h-3 text-black" />
                </div>
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-white font-bold text-lg truncate">{user?.email?.split('@')[0]}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-xs font-medium",
                  isPremium ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-[#2A2A2A] text-[#888]"
                )}>
                  {isPremium ? "Premium" : "Free"}
                </span>
                {profile?.level && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#2A2A2A] text-white">
                    {getLevelDisplay()}
                  </span>
                )}
                {getGenderDisplay() && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#2A2A2A] text-[#888]">
                    {getGenderDisplay()}
                  </span>
                )}
              </div>
              <p className="text-[#666] text-xs mt-2 truncate">{user?.email}</p>
            </div>
          </div>
          
          {/* Premium CTA for free users */}
          {!isPremium && (
            <button
              onClick={() => navigate("/plan")}
              className="w-full mt-4 py-3 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-black font-bold text-sm rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Crown className="w-4 h-4" />
              Desbloquear Premium
            </button>
          )}
        </div>

        {/* Streak Card */}
        <div className="mb-4">
          <StreakCard currentStreak={currentStreak} bestStreak={bestStreak} />
        </div>
        
        {/* Badges */}
        <div className="mb-6">
          <BadgesSection bestStreak={bestStreak} totalWorkouts={totalWorkouts} />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <StatCard 
            icon={Dumbbell} 
            label="Entrenamientos" 
            value={totalWorkouts} 
            unit="total"
            color="text-blue-400"
          />
          <StatCard 
            icon={Calendar} 
            label="Semana actual" 
            value={profile?.current_session_index !== undefined ? profile.current_session_index + 1 : 1}
            unit={`/${profile?.days_per_week || 3} sesiones`}
            color="text-green-400"
          />
          {isPremium && (
            <>
              <StatCard 
                icon={Target} 
                label="Objetivo" 
                value={profile?.objetivo === "definicion" ? "Definición" : 
                       profile?.objetivo === "musculacion" ? "Musculación" : 
                       profile?.objetivo === "recomposicion" ? "Recomposición" : "-"}
                color="text-purple-400"
              />
              <StatCard 
                icon={TrendingUp} 
                label="Ciclos" 
                value={profile?.cycles_completed || 0}
                unit="completados"
                color="text-orange-400"
              />
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="space-y-3">
          <h2 className="text-white font-bold text-base mb-3">Acciones rápidas</h2>
          
          {isPremium && (
            <>
              <QuickActionCard
                icon={Utensils}
                label="Mi nutrición"
                sublabel="Objetivos y plan flexible"
                onClick={() => navigate("/mi-nutricion")}
                highlight
              />
              <QuickActionCard
                icon={ChefHat}
                label="Recetas"
                sublabel="Ideas rápidas para tus comidas"
                onClick={() => navigate("/recetas")}
              />
              <QuickActionCard
                icon={TrendingUp}
                label="Mi progreso"
                sublabel="Check-ins y medidas"
                onClick={() => navigate("/progreso")}
              />
              <QuickActionCard
                icon={Scale}
                label="Nuevo Check-in"
                sublabel="Registra tu estado semanal"
                onClick={() => navigate("/premium/check-in")}
              />
            </>
          )}

          {!isPremium && (
            <QuickActionCard
              icon={Crown}
              label="Pasar a Premium"
              sublabel="Nutrición + Seguimiento"
              onClick={() => navigate("/plan")}
              highlight
            />
          )}
        </div>

        {/* Logout */}
        <button
          onClick={logout}
          className="w-full mt-8 py-3 bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl flex items-center justify-center gap-2 text-[#888] hover:text-white transition-colors"
          data-testid="logout-btn-profile"
        >
          <LogOut className="w-4 h-4" />
          Cerrar sesión
        </button>

        {/* Version */}
        <p className="text-center text-[#444] text-xs mt-4">GOATGYM v3.1 - Premium Polish</p>
      </div>
    </div>
  );
};

export default PerfilPage;
